import { useEffect, useRef, useState } from "react";

import { ONBOARDING_AGENT_STREAM_CHARS_PER_SECOND } from "../../onboardingAgentConstants";

export type UseStreamedAssistantTextResult = {
    readonly visibleText: string;
    readonly isStreaming: boolean;
};

export const useStreamedAssistantText = (
    fullText: string,
    shouldStream: boolean,
    onStreamComplete: () => void,
): UseStreamedAssistantTextResult => {
    const [visibleLength, setVisibleLength] = useState(() => (shouldStream ? 0 : fullText.length));

    const onStreamCompleteRef = useRef(onStreamComplete);
    onStreamCompleteRef.current = onStreamComplete;

    const visibleLengthRef = useRef(visibleLength);

    useEffect(() => {
        if (!shouldStream) {
            visibleLengthRef.current = fullText.length;
            setVisibleLength(fullText.length);
            return;
        }

        if (fullText.length === 0) {
            visibleLengthRef.current = 0;
            setVisibleLength(0);
            onStreamCompleteRef.current();
            return;
        }

        visibleLengthRef.current = 0;
        setVisibleLength(0);

        let rafId: number | null = null;
        let previousTimestamp: number | null = null;

        const tick = (timestamp: number): void => {
            if (previousTimestamp === null) {
                previousTimestamp = timestamp;
                rafId = window.requestAnimationFrame(tick);
                return;
            }

            const elapsedMs = timestamp - previousTimestamp;
            previousTimestamp = timestamp;

            const charsToAdd = Math.max(
                1,
                Math.round((elapsedMs / 1000) * ONBOARDING_AGENT_STREAM_CHARS_PER_SECOND),
            );
            visibleLengthRef.current = Math.min(fullText.length, visibleLengthRef.current + charsToAdd);
            setVisibleLength(visibleLengthRef.current);

            if (visibleLengthRef.current >= fullText.length) {
                onStreamCompleteRef.current();
                return;
            }

            rafId = window.requestAnimationFrame(tick);
        };

        rafId = window.requestAnimationFrame(tick);

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [fullText, shouldStream]);

    return {
        visibleText: fullText.slice(0, visibleLength),
        isStreaming: shouldStream && visibleLength < fullText.length,
    };
};
